import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  X, 
  Search, 
  ShoppingCart, 
  Receipt,
  Plus,
  Minus,
  AlertCircle,
  CheckCircle,
  ExternalLink
} from 'lucide-react';
import { saleReturnsAPI, salesAPI, exchangesAPI, woocommerceAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import dayjs from '../../utils/dayjs';
import WebOrderLookup from '../ui/WebOrderLookup';
import CheckoutInfoModal from '../ui/CheckoutInfoModal';

const SaleReturnForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { formatPrice } = useCurrency();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Sale selection state
  const [availableSales, setAvailableSales] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [saleDetails, setSaleDetails] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [previousReturns, setPreviousReturns] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // Return form state
  const [formData, setFormData] = useState({
    SaleReturnDate: dayjs().format('YYYY-MM-DDTHH:mm'),
    Remarks: '',
    Customer_ID: null,
    Sale_ID: null,
    Exchange_ID: null,
    // Refunds come out of the till by default; expensive items that exceed the
    // cash on hand get paid by bank transfer instead.
    PaymentMethod: 'cash',
  });

  // Refund modal — confirms how the money goes back to the customer.
  const [showRefundModal, setShowRefundModal] = useState(false);

  // Web-order return mode
  const [returnMode, setReturnMode] = useState('in-store'); // 'in-store' | 'web-order'
  const [fetchedOrderRefs, setFetchedOrderRefs] = useState([]);
  // Whether this branch enables the "Web Order" return mode (opt-in via branches.json)
  const [webEnabled, setWebEnabled] = useState(false);

  useEffect(() => {
    woocommerceAPI.getCapabilities()
      .then(res => setWebEnabled(Boolean(res.data?.web_sale_return_enabled)))
      .catch(() => setWebEnabled(false));
  }, []);

  // Return items state
  const [returnItems, setReturnItems] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});

  // Build a map of already returned quantities per SaleItem_ID from previous returns
  const returnedByItemId = useMemo(() => {
    const map = {};
    try {
      (previousReturns || []).forEach((ret) => {
        (ret.details || []).forEach((d) => {
          const key = d.SaleItem_ID;
          const qty = parseFloat(d.Quantity) || 0;
          map[key] = (map[key] || 0) + qty;
        });
      });
    } catch (e) {
      // safe guard: keep map empty if structure unexpected
    }
    return map;
  }, [previousReturns]);

  const getRemainingQty = (saleItemId, originalQty) => {
    const returnedQty = parseFloat(returnedByItemId[saleItemId] || 0) || 0;
    const orig = parseFloat(originalQty) || 0;
    return Math.max(0, orig - returnedQty);
  };

  // A web-order return is driven by the WooCommerce lookup, not by a local sale/exchange.
  const isWebOrderReturn = selectedSale?.__type === 'web';

  // Calculate weighted additional discount allocation based on item values
  const getWeightedAdditionalDiscount = () => {
    // Web orders carry their order-level discount as a fixed per-unit share on every row
    // (see calculateWebReturnDiscount); re-allocating the pot here would double-count it.
    if (isWebOrderReturn) return {};

    const originalAdditionalDiscount = parseFloat(saleDetails?.Discount || 0);
    
    if (originalAdditionalDiscount === 0) return {};
    
    // Calculate remaining additional discount after previous returns
    const usedAdditionalDiscount = previousReturns?.reduce((sum, ret) => sum + (parseFloat(ret.Discount) || 0), 0) || 0;
    const remainingAdditionalDiscount = Math.max(0, originalAdditionalDiscount - usedAdditionalDiscount);
    
    if (remainingAdditionalDiscount === 0) return {};
    
    // Calculate total value of remaining items
    const remainingItems = (saleDetails?.details || []).filter(item => {
      const originalQty = parseFloat(item.Quantity) || 0;
      const remaining = getRemainingQty(item.SaleItem_ID, originalQty);
      return remaining > 0;
    });
    
    if (remainingItems.length === 0) return {};
    
    // Calculate total value of remaining items (using net price after item discount)
    const totalRemainingValue = remainingItems.reduce((sum, item) => {
      const originalQty = parseFloat(item.Quantity) || 0;
      const remaining = getRemainingQty(item.SaleItem_ID, originalQty);
      const netPrice = (parseFloat(item.Price) || 0) - (parseFloat(item.Discount) || 0);
      return sum + (remaining * netPrice);
    }, 0);
    
    if (totalRemainingValue === 0) return {};
    
    // Calculate weighted allocation per item
    const weightedAllocation = {};
    remainingItems.forEach(item => {
      const originalQty = parseFloat(item.Quantity) || 0;
      const remaining = getRemainingQty(item.SaleItem_ID, originalQty);
      const netPrice = (parseFloat(item.Price) || 0) - (parseFloat(item.Discount) || 0);
      const itemValue = remaining * netPrice;
      const weight = itemValue / totalRemainingValue;
      weightedAllocation[item.SaleItem_ID] = weight;
    });

    console.log("weightedAllocation",weightedAllocation, remainingAdditionalDiscount)
    
    return { weightedAllocation, remainingAdditionalDiscount };
  };

  // Calculate return additional discount based on selected items using weighted allocation
  const calculateReturnAdditionalDiscount = () => {
  
    const { weightedAllocation, remainingAdditionalDiscount } = getWeightedAdditionalDiscount();
    
    if (!weightedAllocation || remainingAdditionalDiscount === 0) return 0;
    
    let totalReturnAdditionalDiscount = 0;
    
    (saleDetails?.details || []).forEach(item => {
      const mapEntry = selectedMap[item.SaleItem_ID];
      if (!mapEntry?.selected) return;
      
      const returnedQuantity = parseFloat(mapEntry.quantity) || 0;
      if (returnedQuantity === 0) return;
      
      const originalQty = parseFloat(item.Quantity) || 0;
      const remaining = getRemainingQty(item.SaleItem_ID, originalQty);
      const netPrice = (parseFloat(item.Price) || 0) - (parseFloat(item.Discount) || 0);
      
      // Calculate the proportion of this item's value to total remaining value
      const itemValue = remaining * netPrice;
      const totalRemainingValue = (saleDetails?.details || []).reduce((sum, d) => {
        const origQty = parseFloat(d.Quantity) || 0;
        const rem = getRemainingQty(d.SaleItem_ID, origQty);
        return sum + (rem * ((parseFloat(d.Price) || 0) - (parseFloat(d.Discount) || 0)));
      }, 0);
      
      if (totalRemainingValue === 0) return;
      
      const itemWeight = itemValue / totalRemainingValue;
      const itemAdditionalDiscount = remainingAdditionalDiscount * itemWeight;
      
      // Calculate the proportion of returned quantity to remaining quantity for this item
      const returnProportion = returnedQuantity / remaining;
      const returnAdditionalDiscount = itemAdditionalDiscount * returnProportion;
      
      totalReturnAdditionalDiscount += returnAdditionalDiscount;
    });
    
    return totalReturnAdditionalDiscount;
  };

  // Web-order counterpart of calculateReturnAdditionalDiscount. WooCommerce discounts that
  // never reached the line prices (negative fee lines such as "Online Payment Saving") arrive
  // already split per unit, so a partial return is just quantity × share — no weighting and
  // no "how much was consumed by earlier returns" bookkeeping.
  const calculateWebReturnDiscount = () => {
    return (saleDetails?.details || []).reduce((sum, item) => {
      const mapEntry = selectedMap[item.SaleItem_ID];
      if (!mapEntry?.selected) return sum;

      const remaining = getRemainingQty(item.SaleItem_ID, parseFloat(item.Quantity) || 0);
      const chosenQty = Math.min(
        Math.max(parseFloat(mapEntry.quantity ?? remaining) || 0, 0),
        remaining
      );
      return sum + chosenQty * (parseFloat(item.discountShareUnit) || 0);
    }, 0);
  };

  // The order-level discount this return claims back. The two sources are structurally
  // different and must never mix: an in-store sale holds one pot to spread across items,
  // a web order arrives pre-split per unit.
  const calculateReturnDiscount = () =>
    isWebOrderReturn ? calculateWebReturnDiscount() : calculateReturnAdditionalDiscount();

  // What a past return actually refunded — the same arithmetic its own detail page
  // shows. Listing SaleReturn.Discount here instead would advertise the additional
  // discount as the return's value, which reads far smaller than the money handed over.
  const previousReturnTotal = (ret) => {
    const lines = (ret?.details || []).reduce((sum, d) => {
      const qty = parseFloat(d.Quantity) || 0;
      const net = (parseFloat(d.Price) || 0) - (parseFloat(d.Discount) || 0);
      return sum + qty * net;
    }, 0);
    return lines - (parseFloat(ret?.Discount) || 0);
  };

  // Initialize items as selected by default when sale details or previous returns change
  useEffect(() => {
    const details = saleDetails?.details || [];
    if (details.length > 0) {
      const initial = {};
      details.forEach(d => {
        const originalQty = parseFloat(d.Quantity) || 0;
        const remaining = getRemainingQty(d.SaleItem_ID, originalQty);
        initial[d.SaleItem_ID] = {
          selected: remaining > 0, // Default to selected if there's remaining quantity
          quantity: remaining
        };
      });
      setSelectedMap(initial);
    } else {
      setSelectedMap({});
    }
  }, [saleDetails, returnedByItemId]);

  // Ref for search input
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isEditing) {
      loadSaleReturn();
    }
    // No pre-loading of sales - completely server-driven
  }, [id, isEditing]);

  // Auto-focus search input on page load
  useEffect(() => {
    const focusSearchInput = () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
        searchInputRef.current.select();
      }
    };

    // Focus after a short delay to ensure the component is fully rendered
    const timer = setTimeout(focusSearchInput, 100);
    return () => clearTimeout(timer);
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Trigger search when debounced search term changes
  useEffect(() => {
    if (debouncedSearchTerm !== undefined && debouncedSearchTerm.trim()) {
      loadAvailableSources(debouncedSearchTerm);
      // Show dropdown when searching (but not if a sale is already selected)
      if (!selectedSale) {
        setShowDropdown(true);
      }
    } else if (debouncedSearchTerm === '') {
      // Clear results when search is empty
      setAvailableSales([]);
      setShowDropdown(false);
    }
  }, [debouncedSearchTerm, selectedSale]);

  const loadAvailableSources = async (searchQuery) => {
    try {
      setLoadingSales(true);
      const q = searchQuery.trim();
      const [salesRes, exchangesRes] = await Promise.all([
        salesAPI.list({ limit: 50, q }),
        exchangesAPI.list({ limit: 50, search: q }),
      ]);
      const sales = (salesRes.data || []).map((s) => ({ ...s, __type: 'sale' }));
      const exchanges = (exchangesRes.data || []).map((x) => ({ ...x, __type: 'exchange' }));
      setAvailableSales([...sales, ...exchanges]);
    } catch (error) {
      console.error('Error loading sources:', error);
      setError('Failed to load sales/exchanges');
    } finally {
      setLoadingSales(false);
    }
  };

  const loadSaleReturn = async () => {
    try {
      setLoading(true);
      const response = await saleReturnsAPI.get(id);
      console.log("sale details from the server",response.data)
      const sr = response.data;
      setFormData({
        SaleReturnDate: dayjs(sr.SaleReturnDate).format('YYYY-MM-DDTHH:mm'),
        Remarks: sr.Remarks || '',
        Customer_ID: sr.Customer_ID || null,
        Sale_ID: sr.Sale_ID || null,
        PaymentMethod: sr.PaymentMethod || 'cash',
      });
      
      // Load the original sale details if editing
      if (sr.Sale_ID) {
        await loadSaleDetails(sr.Sale_ID);
        await loadPreviousReturns(sr.Sale_ID);
      }
    } catch (error) {
      console.error('Error loading sale return:', error);
      setError('Failed to load sale return details');
    } finally {
      setLoading(false);
    }
  };

  const loadSaleDetails = async (saleId) => {
    try {
      const response = await salesAPI.getById(saleId);
      console.log("response received from the server",response.data)
      setSaleDetails(response.data || null);
    } catch (error) {
      console.error('Error loading sale details:', error);
      setError('Failed to load sale details');
    }
  };

  const handleSourceSelect = async (src) => {
    if (src.__type === 'exchange') {
      return handleExchangeSelect(src);
    }
    setSelectedSale({ ...src, __type: 'sale' });
    setFormData(prev => ({ ...prev, Sale_ID: src.ID, Exchange_ID: null, Customer_ID: src.Customer_ID || null }));
    await loadSaleDetails(src.ID);
    await loadPreviousReturns(src.ID);
    setSearchTerm('');
    setShowDropdown(false);
  };

  // Map an exchange's GIVEN details into the same row shape the table/totals/submit expect.
  const normalizeExchangeGivenItems = (exchange) => {
    const given = (exchange?.details || []).filter(d => d.ExchangeType === 'GIVEN');
    return {
      Discount: parseFloat(exchange?.GivenItemsDiscountAmount || 0),
      woocommerce_id: null,
      details: given.map(d => ({
        ID: d.ID,
        SaleItem_ID: d.Item_ID,
        Quantity: d.Quantity,
        Price: d.Price,
        Discount: d.Discount,
        item: d.item,
      })),
    };
  };

  const handleExchangeSelect = async (exchange) => {
    setSelectedSale({ ...exchange, __type: 'exchange' });
    setFormData(prev => ({
      ...prev,
      Sale_ID: null,
      Exchange_ID: exchange.ID,
      Customer_ID: exchange.Customer_ID || null,
    }));
    try {
      const resp = await exchangesAPI.get(exchange.ID);
      setSaleDetails(normalizeExchangeGivenItems(resp.data));
    } catch (e) {
      console.error('Error loading exchange details:', e);
      setError('Failed to load exchange details');
    }
    try {
      const prevReturns = await saleReturnsAPI.getByExchange(exchange.ID);
      setPreviousReturns(prevReturns.data || []);
    } catch (e) {
      setPreviousReturns([]);
    }
    setSearchTerm('');
    setShowDropdown(false);
  };

  // Map a fetched web order (single order/brand) into the form's item shape.
  const handleWebItemsFetched = async (newItems, ref) => {
    setSelectedSale({
      __type: 'web',
      ID: ref.woocommerce_order_id,
      brand: ref.brand,
      SaleNo: `Web #${ref.woocommerce_order_id} (${ref.brand})`,
      SaleDate: new Date().toISOString(),
    });
    setFormData(prev => ({
      ...prev,
      Sale_ID: null,
      Exchange_ID: null,
      woocommerce_id: ref.woocommerce_order_id,
      brand: ref.brand,
    }));
    // Availability already accounts for prior returns, but the operator still needs
    // to SEE them — same "Previous Returns" panel the in-store path shows.
    try {
      const prev = await saleReturnsAPI.getByWebOrder(ref.brand, ref.woocommerce_order_id);
      setPreviousReturns(prev.data || []);
    } catch (error) {
      console.error('Error loading previous returns for web order:', error);
      setPreviousReturns([]);
    }
    setSaleDetails({
      // Order-level discount still attached to the units that can be returned. Shown as the
      // pot in the summary panel, but never allocated from — the per-unit shares below are
      // the source of truth (getWeightedAdditionalDiscount opts web orders out).
      Discount: newItems.reduce((sum, it) => sum + it.quantity * (it.discountShareUnit || 0), 0),
      woocommerce_id: ref.woocommerce_order_id,
      brand: ref.brand,
      details: newItems.map(it => ({
        ID: it.Item_ID,
        SaleItem_ID: it.Item_ID,
        Quantity: it.quantity,
        Price: it.price,        // paid_unit_price (usePaidPrice=true)
        Discount: 0,
        // Slice of the order-level discount this unit carries (WC negative fee lines)
        discountShareUnit: it.discountShareUnit || 0,
        // ordered / alreadyReturned: snapshot from the WC lookup (re-fetch to refresh)
        ordered: it.ordered,
        alreadyReturned: it.alreadyReturned,
        item: { Name: it.item?.Name, Barcode: it.item?.product_id },
      })),
    });
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (availableSales.length > 0) {
        handleSourceSelect(availableSales[0]);
      }
    }
  };

  const loadPreviousReturns = async (saleId) => {
    try {
      const resp = await saleReturnsAPI.getBySale(saleId);
      console.log("previous returns",resp.data)
      setPreviousReturns(resp.data || []);
    } catch (err) {
      console.error('Error loading previous returns:', err);
      setPreviousReturns([]);
    }
  };

  const handleSearchFocus = () => {
    if (searchInputRef.current) {
      searchInputRef.current.select();
    }
    setShowDropdown(true); // Show dropdown when focused
  };

  const handleSearchBlur = () => {
    // Delay hiding to allow for click events on dropdown items
    setTimeout(() => {
      setShowDropdown(false);
    }, 150);
  };

  const handleReturnItemChange = (itemId, field, value) => {
    setReturnItems(prev => {
      const existing = prev.find(item => item.SaleItem_ID === itemId);
      if (existing) {
        return prev.map(item => 
          item.SaleItem_ID === itemId 
            ? { ...item, [field]: value }
            : item
        );
      } else {
        return [...prev, { SaleItem_ID: itemId, [field]: value }];
      }
    });
  };

  // Handle select all/deselect all functionality
  const handleSelectAll = (checked) => {
    const details = saleDetails?.details || [];
    const newSelectedMap = {};
    
    details.forEach(item => {
      const originalQty = parseFloat(item.Quantity) || 0;
      const remaining = getRemainingQty(item.SaleItem_ID, originalQty);
      
      newSelectedMap[item.SaleItem_ID] = {
        selected: checked && remaining > 0,
        quantity: remaining
      };
    });
    
    setSelectedMap(newSelectedMap);
  };

  // Calculate select all checkbox state
  const getSelectAllState = () => {
    const details = saleDetails?.details || [];
    if (details.length === 0) return { checked: false, indeterminate: false };
    
    const selectableItems = details.filter(item => {
      const originalQty = parseFloat(item.Quantity) || 0;
      const remaining = getRemainingQty(item.SaleItem_ID, originalQty);
      return remaining > 0;
    });
    
    if (selectableItems.length === 0) return { checked: false, indeterminate: false };
    
    const selectedCount = selectableItems.filter(item => 
      selectedMap[item.SaleItem_ID]?.selected
    ).length;
    
    if (selectedCount === 0) return { checked: false, indeterminate: false };
    if (selectedCount === selectableItems.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  const calculateTotals = () => {
    // Recalculate based on selected items with proportional additional discount
    let subtotal = 0;
    let totalDiscount = 0;
    let total = 0;
    const returnAdditionalDiscount = calculateReturnDiscount();
    let finalTotal = 0;

    const details = saleDetails?.details || [];
    for (const saleItem of details) {
      const mapEntry = selectedMap[saleItem.SaleItem_ID];
      if (!mapEntry || !mapEntry.selected) continue;
      const originalQty = parseFloat(saleItem.Quantity) || 0;
      const remaining = getRemainingQty(saleItem.SaleItem_ID, originalQty);
      const chosenQty = Math.min(
        Math.max(parseFloat(mapEntry.quantity ?? remaining) || 0, 0),
        remaining
      );
      const priceForQty = (parseFloat(saleItem.Price) || 0) * chosenQty;
      const perItemDiscount = parseFloat(saleItem.Discount) || 0;
      const discountForQty = perItemDiscount * chosenQty;

      total += priceForQty;
      totalDiscount += discountForQty;
      subtotal += priceForQty - discountForQty;
    }

    finalTotal = total - (totalDiscount + returnAdditionalDiscount);
    return { subtotal, totalDiscount, total, additionalDiscount: returnAdditionalDiscount, finalTotal };
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  // Submitting opens the refund modal; the actual save happens on its Confirm,
  // so the refund mode is always an explicit choice at the point of payout.
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedSale) {
      setError('Please select a sale first');
      return;
    }

    setError('');
    setShowRefundModal(true);
  };

  const handleConfirmRefund = async () => {
    if (!selectedSale) {
      setError('Please select a sale first');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const details = (saleDetails?.details || [])
        .filter(sd => selectedMap[sd.SaleItem_ID]?.selected && (selectedMap[sd.SaleItem_ID]?.quantity ?? 0) > 0)
        .map(sd => {
          const originalQty = parseFloat(sd.Quantity) || 0;
          const remaining = getRemainingQty(sd.SaleItem_ID, originalQty);
          const chosenQty = Math.min(
            Math.max(parseFloat(selectedMap[sd.SaleItem_ID]?.quantity ?? remaining) || 0, 0),
            remaining
          );
          return {
            SaleItem_ID: sd.SaleItem_ID,
            Price: sd.Price,
            Quantity: chosenQty,
            Discount: sd.Discount
          }
        })

      console.log("formData.Customer_ID ",formData.Customer_ID)

      const submitData = {
        ...formData,
        SaleReturnDate: new Date(formData.SaleReturnDate).toISOString(),
        Customer_ID: formData.Customer_ID ? parseInt(formData.Customer_ID) : null,
        Sale_ID: formData.Sale_ID ? parseInt(formData.Sale_ID) : null,
        Exchange_ID: formData.Exchange_ID ? parseInt(formData.Exchange_ID) : null,
        details: details,
        Discount: calculateReturnDiscount(),
        woocommerce_id: formData.woocommerce_id || saleDetails?.woocommerce_id || null,
        brand: formData.brand || saleDetails?.brand || null,
      };

      if (isEditing) {
        await saleReturnsAPI.update(id, submitData);
        setSuccess('Sale return updated successfully!');
      } else {
        await saleReturnsAPI.create(submitData);
        setSuccess('Sale return created successfully!');
      }

      setShowRefundModal(false);

      setTimeout(() => {
        navigate('/sale-return');
      }, 1500);

    } catch (error) {
      console.error('Error saving sale return:', error);
      setError(error.response?.data?.detail || 'Failed to save sale return');
      setShowRefundModal(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/sale-return');
  };

  // Use availableSales directly since we're doing server-side search
  // No need for filteredSales variable anymore

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const { subtotal, totalDiscount, netAmount, total, finalTotal, additionalDiscount } = calculateTotals();

  return (
    <div className="w-full p-6 min-h-screen bg-white">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Receipt className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isEditing ? 'Edit Sale Return' : 'Create New Sale Return'}
              </h1>
              <p className="text-gray-600">
                {isEditing ? 'Update sale return details' : 'Create a new sale return record'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          {success}
        </div>
      )}
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        {/* Sale Selection */}
        <div className="mb-6 relative">
          {selectedSale && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900">
                    {selectedSale.__type === 'exchange'
                      ? (selectedSale.ExchangeNo || `Exchange #${selectedSale.ID}`)
                      : (selectedSale.SaleNo || `Sale #${selectedSale.ID}`)}
                  </h3>
                  <p className="text-sm text-green-700">
                    {selectedSale.CustomerName || 'Counter Sale'} • {dayjs(selectedSale.SaleDate).format('DD MMM YYYY')}
                  </p>
                </div>
        
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSale(null);
                    setSaleDetails([]);
                    setPreviousReturns([]);
                    setSearchTerm('');
                    setShowDropdown(false);
                    setReturnMode('in-store');
                    setFetchedOrderRefs([]);
                    setFormData(prev => ({
                      ...prev,
                      Sale_ID: null,
                      Exchange_ID: null,
                      Customer_ID: null,
                      woocommerce_id: null,
                      brand: null,
                    }));
                  }}
                  className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                  title="Change sale selection"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          
          {!selectedSale && webEnabled && (
            <div className="flex items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() => setReturnMode('in-store')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  returnMode === 'in-store' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                In-store
              </button>
              <button
                type="button"
                onClick={() => setReturnMode('web-order')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  returnMode === 'web-order' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Web Order
              </button>
            </div>
          )}

          {!selectedSale && returnMode === 'web-order' && (
            <WebOrderLookup
              usePaidPrice={true}
              fetchedOrderRefs={fetchedOrderRefs}
              returnedItems={[]}
              disabled={saving}
              onItemsFetched={(newItems) => {
                const ref = newItems[0]?.webOrderRef;
                if (ref) handleWebItemsFetched(newItems, ref);
              }}
              onOrderRefAdded={(ref) => setFetchedOrderRefs([ref])}
              onCustomerInfo={() => {}}
            />
          )}

          {!selectedSale && returnMode === 'in-store' && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
              ref={searchInputRef}
              type="text"
              placeholder="Search for a sale by number, customer name, or phone..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                // Hide dropdown if search is cleared and no sale is selected
                if (!e.target.value.trim() && !selectedSale) {
                  setShowDropdown(false);
                }
              }}
              onKeyDown={handleSearchKeyDown}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {loadingSales && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
          )}
          
          {/* Sales Dropdown */}
          {!selectedSale && returnMode === 'in-store' && showDropdown && debouncedSearchTerm && (
            <div 
              className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-y-auto"
              onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking inside dropdown
            >
              {availableSales.length > 0 ? (
                availableSales.map((src) => (
                <div
                  key={`${src.__type}-${src.ID}`}
                  onClick={() => handleSourceSelect(src)}
                  className="p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <ShoppingCart className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${src.__type === 'exchange' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {src.__type === 'exchange' ? 'EXCHANGE' : 'SALE'}
                        </span>
                        <h3 className="font-semibold text-gray-900">
                          {src.__type === 'exchange'
                            ? (src.ExchangeNo || `Exchange #${src.ID}`)
                            : (src.SaleNo || `Sale #${src.ID}`)}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-500">
                        {(src.CustomerName || 'Counter')} • {dayjs(src.__type === 'exchange' ? src.ExchangeDate : src.SaleDate).format('DD MMM YYYY')}
                      </p>
                    </div>
                  </div>
                </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  <div className="flex items-center justify-center gap-2">
                    <Search className="h-4 w-4" />
                    <span>No sales found matching "{debouncedSearchTerm}"</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sale Details and Return Items */}
        {selectedSale && saleDetails?.details && saleDetails?.details.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sale Items List - Left Half */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Sale Items ({saleDetails?.details.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            checked={getSelectAllState().checked}
                            ref={(el) => {
                              if (el) el.indeterminate = getSelectAllState().indeterminate;
                            }}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            title="Select/Deselect all items"
                          />
                          <span>Select All</span>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Item</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Qty</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Price</th>
              
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Discount</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Sub Total</th>
                    
                    </tr>
                  </thead>
                  <tbody>
                    {saleDetails?.details.map((item, index) => {
                      const selected = Boolean(selectedMap[item.SaleItem_ID]?.selected);
                      const originalQty = parseFloat(item.Quantity) || 0;
                      const remainingQty = getRemainingQty(item.SaleItem_ID, originalQty);
                      const chosenQtyRaw = selectedMap[item.SaleItem_ID]?.quantity;
                      const chosenQty = selected
                        ? Math.min(Math.max(parseFloat(chosenQtyRaw ?? remainingQty) || 0, 0), remainingQty)
                        : remainingQty;
                      const subtotal = chosenQty * parseFloat(item.Price);
                      const discount = (parseFloat(item.Discount) || 0);
                      const total = subtotal - (discount * chosenQty || 0);
          
                      
                      return (
                        <tr key={item.ID} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => {
                                const isSelected = e.target.checked;
                                setSelectedMap(prev => ({
                                  ...prev,
                                  [item.SaleItem_ID]: {
                                    selected: isSelected && remainingQty > 0,
                                    quantity: isSelected ? (prev[item.SaleItem_ID]?.quantity ?? remainingQty) : (prev[item.SaleItem_ID]?.quantity ?? remainingQty)
                                  }
                                }));
                              }}
                              disabled={remainingQty <= 0}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-900">
                                {item.item?.Name || `Item ${item.SaleItem_ID}`}
                              </p>
                              <p className="text-xs text-gray-500">Barcode: {item.item?.Barcode}</p>
                              {item.ordered != null && (
                                <p className="text-xs text-gray-500">
                                  Ordered {item.ordered} · Returned {item.alreadyReturned} · Available {remainingQty}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {selected ? (
                              <input
                                type="number"
                                value={chosenQty}
                                min={0}
                                max={remainingQty}
                                onChange={(e) => {
                                  const nextVal = e.target.value;
                                  setSelectedMap(prev => ({
                                    ...prev,
                                    [item.SaleItem_ID]: {
                                      selected: true,
                                      quantity: nextVal
                                    }
                                  }));
                                }}
                                className="w-20 px-2 py-1 text-right border border-gray-300 rounded-md text-sm"
                              />
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {remainingQty}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-semibold text-green-600">
                              {formatPrice(item.Price)}
                            </span>
                          </td>
  
                          <td className="px-4 py-3 text-right">
                            <span className="font-semibold text-red-600">
                              -{formatPrice(discount)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-bold text-blue-600">
                              {formatPrice(total)}
                            </span>
                          </td>
                          
                          {/* Remaining hint */}
                          <td className="px-4 py-3 text-right">
                            <span className="text-xs text-gray-500">Remaining: {remainingQty}</span>
                          </td>
                          {/* <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleReturnItemChange(item.SaleItem_ID, 'Quantity', Math.max(0, returnQuantity - 1))}
                                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                                disabled={returnQuantity <= 0}
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <input
                                type="number"
                                value={returnQuantity}
                                onChange={(e) => handleReturnItemChange(item.SaleItem_ID, 'Quantity', parseInt(e.target.value) || 0)}
                                min="0"
                                max={item.Quantity}
                                className="w-16 px-2 py-1 text-center border border-gray-300 rounded-md text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => handleReturnItemChange(item.SaleItem_ID, 'Quantity', Math.min(item.Quantity, returnQuantity + 1))}
                                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                                disabled={returnQuantity >= item.Quantity}
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                            {returnQuantity > 0 && (
                              <div className="mt-2">
                                <input
                                  type="number"
                                  placeholder="Discount"
                                  value={returnDiscount}
                                  onChange={(e) => handleReturnItemChange(item.SaleItem_ID, 'Discount', parseFloat(e.target.value) || 0)}
                                  min="0"
                                  step="0.01"
                                  className="w-full px-2 py-1 text-center border border-gray-300 rounded-md text-xs"
                                />
                              </div>
                            )}
                          </td> */}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Return Summary - Right Half */}
            <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Return Summary
                </h3>
              </div>
              
              <div className="p-6">
                {/* Additional Discount Info */}
                {saleDetails && (
                  <div className="mb-6">
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-sm font-medium text-blue-900 mb-2">Additional Discount Info</div>
                      <div className="space-y-1 text-xs text-blue-700">
                        {/* A web order's pot arrives from the API already net of earlier
                            returns (synced ones via the WC fee line, the rest deducted
                            server-side) — subtracting them again here would double-count.
                            An in-store sale keeps its original pot, so there the used
                            amount must be deducted. */}
                        <div>
                          {isWebOrderReturn ? 'Additional Discount Remaining: ' : 'Original Additional Discount: '}
                          {formatPrice(saleDetails.Discount || 0)}
                        </div>
                        <div>Weighted Allocation: Based on item values</div>
                        {previousReturns && previousReturns.length > 0 && (
                          <div>Used in Previous Returns: {formatPrice(previousReturns.reduce((sum, ret) => sum + (parseFloat(ret.Discount) || 0), 0))}</div>
                        )}
                        {!isWebOrderReturn && (
                          <div>Remaining Available: {formatPrice(Math.max(0, (saleDetails.Discount || 0) - (previousReturns?.reduce((sum, ret) => sum + (parseFloat(ret.Discount) || 0), 0) || 0)))}</div>
                        )}
                        <div>Remaining Quantity: {(saleDetails?.details || []).reduce((sum, item) => {
                          const originalQty = parseFloat(item.Quantity) || 0;
                          const remaining = getRemainingQty(item.SaleItem_ID, originalQty);
                          return sum + remaining;
                        }, 0)}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Previous Returns for this Sale */}
                {previousReturns && previousReturns.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Previous Returns</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">{previousReturns.length}</span>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-auto">
                      {previousReturns.map((r) => (
                        <div 
                          key={r.ID} 
                          className="p-2 border border-gray-200 rounded-lg text-sm flex items-center justify-between hover:bg-gray-50 cursor-pointer transition-colors group"
                          onClick={() => window.open(`/sale-return/${r.ID}`, '_blank')}
                          title="Click to view return details in new tab"
                        >
                          <div className="truncate flex-1">
                            <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">Return #{r.ID}</div>
                            <div className="text-xs text-gray-500">{dayjs(r.SaleReturnDate).format('DD MMM YYYY, HH:mm')}</div>
                          </div>
                          <div className="text-right text-xs text-gray-600 flex items-center gap-2">
                            <div>
                              <div>{Array.isArray(r.details) ? `${r.details.length} item(s)` : ''}</div>
                              <div className="text-red-600">-{formatPrice(previousReturnTotal(r))}</div>
                            </div>
                            <ExternalLink className="h-3 w-3 text-gray-400 group-hover:text-blue-500 transition-colors" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Return Details */}

                {/* Totals */}
                <div className="mb-6 space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-700">Total:</span>
                    <span className="font-semibold text-gray-900">
                      {formatPrice(total)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span className="font-medium text-gray-700">Discount:</span>
                    <span className="font-semibold text-red-600">
                      -{formatPrice(totalDiscount)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span className="font-medium text-gray-700">Return Additional Discount:</span>
                    <span className="font-semibold text-red-600">
                      -{formatPrice(additionalDiscount)}
                    </span>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                      <span className="text-lg font-bold text-blue-900">Net Amount:</span>
                      <span className="text-xl font-bold text-blue-600">
                        {formatPrice(finalTotal)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Remarks */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remarks
                  </label>
                  <textarea
                    rows={3}
                    value={formData.Remarks}
                    onChange={(e) => handleInputChange('Remarks', e.target.value)}
                    placeholder="Enter any additional notes or remarks..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                    disabled={saving}
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving || !selectedSale}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        {isEditing ? 'Update' : 'Create'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>

      <CheckoutInfoModal
        isOpen={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        onConfirm={handleConfirmRefund}
        netAmount={finalTotal}
        checkoutType="sale-return"
        title="Refund Info"
        confirmButtonText={isEditing ? 'Update' : 'Confirm'}
        initialPaymentMode={formData.PaymentMethod}
        onPaymentModeChange={(mode) => handleInputChange('PaymentMethod', mode)}
        disabled={saving}
      />
    </div>
  );
};

export default SaleReturnForm;
